import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Award, ChevronRight, Loader2, RefreshCw, AlertTriangle, Shield, Clock, MapPin, AlertCircle } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, RadialBarChart, RadialBar } from "recharts";

const WINDOWS = [
  { d: 7, label: "7 jours" },
  { d: 30, label: "30 jours" },
  { d: 90, label: "90 jours" },
];

export default function DriverPerformance() {
  const [drivers, setDrivers] = useState([]);
  const [days, setDays] = useState(7);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/driver-scores?days=${days}`);
      setDrivers(data);
      if (!selected && data.length) setSelected(data[0].id);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [days, selected]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) return;
    api.get(`/driver-scores/${selected}?days=${days}`).then(({ data }) => setDetail(data)).catch(() => {});
  }, [selected, days]);

  const tone = (v) => v >= 80 ? "text-green-700" : v >= 60 ? "text-orange-700" : "text-red-700";

  return (
    <div className="p-4 lg:p-6" data-testid="driver-performance-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">RH · Performance</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Performance chauffeurs</h1>
          <p className="text-sm text-zinc-600 mt-1">Score 0-100 sur ponctualité (30) · sécurité (30) · itinéraire (20) · arrêts (10) · incidents (10).</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="border border-zinc-900 grid grid-cols-3 text-[10px] font-bold uppercase tracking-widest">
            {WINDOWS.map((w) => (
              <button key={w.d} onClick={() => setDays(w.d)} data-testid={`win-${w.d}`}
                className={`px-3 py-1.5 border-r border-zinc-900 last:border-r-0 ${days === w.d ? "bg-[#FFC60A]" : "bg-white"}`}>{w.label}</button>
            ))}
          </div>
          <button onClick={load} data-testid="refresh-perf"
            className="inline-flex items-center gap-2 border border-zinc-900 px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Rafraîchir
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-0 border border-zinc-900 min-h-[60vh]">
        <aside className="lg:col-span-4 border-r border-zinc-900 max-h-[70vh] overflow-y-auto" data-testid="drivers-list">
          <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest">Classement ({drivers.length})</div>
          {drivers.length === 0 && <div className="p-6 text-xs text-zinc-500 text-center">Aucun chauffeur.</div>}
          {drivers.map((d, i) => (
            <button key={d.id} data-testid={`driver-${d.id}`} onClick={() => setSelected(d.id)}
              className={`w-full text-left border-b border-zinc-200 p-3 flex items-center gap-3 ${selected === d.id ? "bg-[#FFF7D6]" : "hover:bg-zinc-50"}`}>
              <div className={`w-9 h-9 flex items-center justify-center font-display font-black text-xs border border-zinc-900 ${i === 0 ? "bg-[#FFC60A]" : "bg-white"}`}>#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold tracking-tight truncate">{d.name}</div>
                <div className="text-[10px] font-mono text-zinc-500">{d.email}</div>
              </div>
              <div className={`font-display font-black text-2xl ${tone(d.global)}`}>{d.global}</div>
              <ChevronRight size={14} className="text-zinc-400 shrink-0" />
            </button>
          ))}
        </aside>

        <section className="lg:col-span-8 p-5" data-testid="driver-detail">
          {!detail && <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Sélectionnez un chauffeur…</div>}
          {detail && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
                <ScoreCard label="Score global" value={detail.score.global} icon={Award} highlight />
                <ScoreCard label="Ponctualité /30" value={detail.score.punctuality} icon={Clock} />
                <ScoreCard label="Sécurité /30" value={detail.score.safety} icon={Shield} />
                <ScoreCard label="Itinéraire /20" value={detail.score.route} icon={MapPin} />
                <ScoreCard label="Incidents /10" value={detail.score.incidents} icon={AlertCircle} />
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <div className="border border-zinc-900">
                  <div className="px-4 py-2 border-b border-zinc-900 bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest">Évolution score global ({detail.days}j)</div>
                  <div className="p-3">
                    <ResponsiveContainer width="100%" height={220}>
                      <AreaChart data={detail.timeline}>
                        <defs>
                          <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FFC60A" stopOpacity={0.85} />
                            <stop offset="100%" stopColor="#FFC60A" stopOpacity={0.1} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fontFamily: "monospace" }} stroke="#52525B" />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 9, fontFamily: "monospace" }} stroke="#52525B" />
                        <Tooltip wrapperStyle={{ border: "1px solid #0A0A0B", fontFamily: "monospace", fontSize: 11 }} />
                        <Area type="monotone" dataKey="global" stroke="#0A0A0B" strokeWidth={2} fill="url(#g)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="border border-zinc-900">
                  <div className="px-4 py-2 border-b border-zinc-900 bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest">Événements de conduite</div>
                  <div className="p-3">
                    {Object.keys(detail.events_summary).length === 0 && <div className="text-xs text-zinc-500 py-8 text-center">Aucun événement détecté 🎉</div>}
                    {Object.entries(detail.events_summary).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0" data-testid={`event-${type}`}>
                        <div className="flex items-center gap-2">
                          <AlertTriangle size={14} className="text-orange-500" />
                          <span className="font-display font-bold capitalize">{type.replace("_", " ")}</span>
                        </div>
                        <span className="font-mono text-sm text-zinc-700">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                <Stat label="Missions" value={detail.missions_total} />
                <Stat label="Terminées" value={detail.missions_completed} />
                <Stat label="Incidents ouverts" value={detail.incidents_total} />
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

function ScoreCard({ label, value, icon: Icon, highlight = false }) {
  const tone = value >= 80 ? "text-green-700" : value >= 60 ? "text-orange-700" : "text-red-700";
  return (
    <div className={`border border-zinc-900 p-3 ${highlight ? "bg-[#FFC60A]" : "bg-white"}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-700">{label}</div>
        <Icon size={14} className="text-zinc-700" />
      </div>
      <div className={`mt-2 font-display font-black text-3xl tracking-tighter ${highlight ? "text-zinc-900" : tone}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-zinc-900 p-3 bg-white">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 font-display font-black text-2xl tracking-tighter">{value}</div>
    </div>
  );
}
